import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText } from 'lucide-react';

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(email, password, name);
      } else {
        await login(email, password);
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-[#284A33] mb-5">
            <FileText className="text-white" size={28} />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1C1C19] mb-2">
            StatementSort
          </h1>
          <p className="text-[#5C5C55] text-base">
            Parse statements. Tag expenses. Track spending.
          </p>
        </div>

        <div className="bg-white border border-[#E2E0D8] rounded-md p-8">
          <h2 className="font-heading text-2xl tracking-tight text-[#1C1C19] mb-6">
            {isRegister ? 'Create Account' : 'Sign In'}
          </h2>

          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="border-[#E2E0D8] focus:border-[#284A33]"
                  required
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Email</Label>
              <Input
                data-testid="auth-email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="border-[#E2E0D8] focus:border-[#284A33]"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Password</Label>
              <Input
                data-testid="auth-password-input"
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="border-[#E2E0D8] focus:border-[#284A33]"
                required
                minLength={6}
              />
            </div>
            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#284A33] text-white hover:bg-[#1C3624] rounded-md font-medium transition-colors h-11"
            >
              {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="auth-toggle-button"
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-sm text-[#284A33] hover:text-[#1C3624] transition-colors font-medium"
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
